import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Chip from '@mui/material/Chip';
import Fade from '@mui/material/Fade';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import FavoriteIcon from '@mui/icons-material/Favorite';
import BackpackIcon from '@mui/icons-material/Backpack';
import BusinessIcon from '@mui/icons-material/Business';
import CelebrationIcon from '@mui/icons-material/Celebration';
import BuildingTripLoader from './BuildingTripLoader';
import * as api from '../api';
import { TripBuildError } from '../api';
import * as analytics from '../analytics';
import { useSnackbar } from '../hooks';

interface TripSample {
  id: string;
  label: string;
  icon: React.ReactElement;
  text: string;
  color: 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'error';
}

const tripSamples: TripSample[] = [
  {
    id: 'culture',
    label: 'Culture & Food',
    icon: <FlightTakeoffIcon fontSize="small" />,
    color: 'primary',
    text: 'I want to visit Italy for 10 days and I am interested in seeing Rome, Venice and Milan. I like to visit museums and love to eat italian food. My budget is about USD 10K.',
  },
  {
    id: 'family',
    label: 'Family Adventure',
    icon: <FamilyRestroomIcon fontSize="small" />,
    color: 'success',
    text: 'Planning a 2-week family trip to Spain with 2 kids (ages 8 and 12). We want to visit Barcelona and Madrid, enjoy beaches, theme parks, and kid-friendly attractions. Budget around $8,000.',
  },
  {
    id: 'romantic',
    label: 'Romantic Getaway',
    icon: <FavoriteIcon fontSize="small" />,
    color: 'error',
    text: 'Looking for a romantic 7-day trip to France for our anniversary. We love wine tasting, scenic views, and fine dining. Interested in Paris, Lyon, and the French Riviera. Budget is flexible, around $5,000.',
  },
  {
    id: 'backpacker',
    label: 'Alpine Adventure',
    icon: <BackpackIcon fontSize="small" />,
    color: 'warning',
    text: 'Solo hiking trip through Switzerland for 10 days. Want to visit Zermatt, Interlaken, and Lucerne. Looking for mountain trails, scenic lakes, and charming villages. Budget around $4,000.',
  },
  {
    id: 'business',
    label: 'Business + Leisure',
    icon: <BusinessIcon fontSize="small" />,
    color: 'info',
    text: 'I have a conference in Milan for 3 days and want to extend it to a week to explore Northern Italy. Interested in Lake Como day trips, good restaurants, and fashion shopping. Mix of business and boutique hotels.',
  },
  {
    id: 'celebration',
    label: 'Special Celebration',
    icon: <CelebrationIcon fontSize="small" />,
    color: 'secondary',
    text: 'Planning a milestone birthday trip to the South of France for 10 days with a group of 6 friends. We want to explore Nice, Cannes, and Monaco. Love beaches, nightlife, and good food. Budget $12K total.',
  },
];

interface TextTripBuilderProps {
  onBack: () => void;
  onTripBuilt: (tripData: any) => void;
}

function TextTripBuilder({ onBack, onTripBuilt }: TextTripBuilderProps) {
  const [tripDescription, setTripDescription] = useState('');
  const [selectedSample, setSelectedSample] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [preserveCityOrder, setPreserveCityOrder] = useState(false);
  const snackbar = useSnackbar();

  const handleSampleClick = (sample: TripSample) => {
    setTripDescription(sample.text);
    setSelectedSample(sample.id);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTripDescription(e.target.value);
    // Clear sample selection if user modifies the text
    if (selectedSample) {
      const currentSample = tripSamples.find(s => s.id === selectedSample);
      if (currentSample && e.target.value !== currentSample.text) {
        setSelectedSample(null);
      }
    }
  };

  const handleBuildTrip = async () => {
    if (!tripDescription.trim()) {
      snackbar.showError('Please describe your trip before building');
      return;
    }

    setLoading(true);
    const startTime = Date.now();

    analytics.trackBuildTripFromTextStarted(tripDescription.length);

    try {
      const data = await api.buildTripFromText({ text: tripDescription, preserve_city_order: preserveCityOrder });

      const duration = Date.now() - startTime;
      analytics.trackBuildTripFromTextCompleted(data.trip?.id || 0, duration);

      onTripBuilt(data);
    } catch (err) {
      console.error('Error building trip from text:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to build trip';
      analytics.trackBuildTripFromTextFailed(errorMessage);
      snackbar.showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <AutoAwesomeIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: 'primary.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom fontWeight={600} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Describe Your Dream Trip
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto', px: { xs: 2, sm: 0 } }}>
          Tell us what you're looking for in your own words, and our AI will craft the perfect itinerary for you
        </Typography>
      </Box>

      {/* Sample Chips */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
          Try an example or write your own:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {tripSamples.map((sample) => (
            <Chip
              key={sample.id}
              label={sample.label}
              icon={sample.icon}
              onClick={() => handleSampleClick(sample)}
              color={selectedSample === sample.id ? 'primary' : 'default'}
              variant={selectedSample === sample.id ? 'filled' : 'outlined'}
              sx={{
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 2,
                },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Text Input */}
      <Fade in={true} timeout={300}>
        <Paper
          elevation={0}
          sx={{
            border: '2px solid',
            borderColor: tripDescription ? 'primary.main' : 'grey.300',
            borderRadius: 3,
            overflow: 'hidden',
            transition: 'border-color 0.3s ease',
            mb: 3,
          }}
        >
          <TextField
            fullWidth
            multiline
            rows={7}
            value={tripDescription}
            onChange={handleTextChange}
            placeholder="Example: I want to explore Japan for 2 weeks, visiting Tokyo, Kyoto, and Osaka. I'm interested in temples, cherry blossoms, anime culture, and authentic ramen. Traveling with my partner, budget around $6,000..."
            variant="standard"
            InputProps={{
              disableUnderline: true,
              sx: {
                p: 2.5,
                fontSize: '1.05rem',
                lineHeight: 1.7,
              },
            }}
          />
          {tripDescription && (
            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                bgcolor: 'grey.50',
                borderTop: '1px solid',
                borderColor: 'grey.200',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {tripDescription.length} characters
              </Typography>
              <Button
                size="small"
                onClick={() => {
                  setTripDescription('');
                  setSelectedSample(null);
                }}
                sx={{ textTransform: 'none' }}
              >
                Clear
              </Button>
            </Box>
          )}
        </Paper>
      </Fade>

      {/* Tips */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 4,
          bgcolor: 'primary.50',
          borderRadius: 2,
        }}
      >
        <Typography variant="subtitle2" color="primary.dark" gutterBottom fontWeight={600}>
          Tips for best results:
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary' }}>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
            Include your destination country and cities of interest
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
            Mention how many days you plan to travel
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
            Share your interests (food, culture, adventure, relaxation)
          </Typography>
          <Typography component="li" variant="body2">
            Add your approximate budget if you have one in mind
          </Typography>
        </Box>
      </Paper>

      {/* City Order Toggle */}
      <Box sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={preserveCityOrder}
              onChange={(_, checked) => setPreserveCityOrder(checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              Preserve city order
            </Typography>
          }
        />
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, justifyContent: 'space-between', gap: 2 }}>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          disabled={loading}
          sx={{ px: 3 }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleBuildTrip}
          disabled={loading || !tripDescription.trim()}
          size="large"
          endIcon={<AutoAwesomeIcon />}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 600,
            boxShadow: 3,
            '&:hover': {
              boxShadow: 6,
            },
          }}
        >
          Create My Trip
        </Button>
      </Box>

      <BuildingTripLoader open={loading} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={snackbar.close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={snackbar.close} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default TextTripBuilder;
